import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Minus, Plus, Trash2, ShoppingBag, ArrowRight, Lock,
  Truck, CheckCircle2, CreditCard, ShieldCheck,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { FREE_SHIP_THRESHOLD, PRODUCTS } from "../mock";
import { useToast } from "../hooks/use-toast";
import VialRender from "../components/VialRender";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, clearCart, count } = useCart();
  const { toast } = useToast();
  const [placing, setPlacing] = useState(false);

  const shipping = subtotal >= FREE_SHIP_THRESHOLD || subtotal === 0 ? 0 : 14.95;
  const estTotal = subtotal + shipping;
  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);

  const checkout = async () => {
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ lookup_key: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      setPlacing(false);
      toast({
        title: "Checkout failed",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (count === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <div className="mx-auto h-20 w-20 rounded-full bg-blue-50 flex items-center justify-center">
          <ShoppingBag className="h-9 w-9 text-blue-600" />
        </div>
        <h1 className="mt-6 font-display font-extrabold text-3xl text-slate-900">Your cart is empty</h1>
        <p className="mt-2 text-slate-500">Add some research compounds to get started.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white font-semibold px-6 py-3 hover:bg-blue-700 transition-colors">
          Browse Products <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display font-extrabold text-3xl text-slate-900 mb-6">Shopping Cart</h1>

      {/* free ship progress */}
      <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Truck className="h-4 w-4 text-blue-600" />
          {remaining > 0 ? (
            <span>Add <span className="font-bold text-blue-700">${remaining.toFixed(2)}</span> more for free express shipping.</span>
          ) : (
            <span className="font-semibold text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> You've unlocked free express shipping!</span>
          )}
        </div>
        <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.id} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-4">
              <Link to={`/product/${it.id}`} className="h-24 w-24 rounded-xl overflow-hidden shrink-0 border border-slate-200">
                <VialRender product={PRODUCTS.find((p) => p.id === it.id) || it} />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Link to={`/product/${it.id}`} className="font-display font-bold text-slate-900 hover:text-blue-700 line-clamp-1">{it.name}</Link>
                    <p className="text-xs text-slate-500 mt-0.5">{it.size} · AUD per vial</p>
                  </div>
                  <button onClick={() => removeItem(it.id)} className="text-slate-400 hover:text-red-500 transition-colors" aria-label="Remove">
                    <Trash2 className="h-4.5 w-4.5" />
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="inline-flex items-center rounded-lg border border-slate-200">
                    <button onClick={() => updateQty(it.id, it.qty - 1)} className="h-9 w-9 flex items-center justify-center text-slate-600 hover:text-blue-600" aria-label="Decrease">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-bold text-slate-900">{it.qty}</span>
                    <button onClick={() => updateQty(it.id, it.qty + 1)} className="h-9 w-9 flex items-center justify-center text-slate-600 hover:text-blue-600" aria-label="Increase">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="font-extrabold text-slate-900">${(it.price * it.qty).toFixed(2)}</div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => { clearCart(); toast({ title: "Cart cleared" }); }} className="text-sm text-slate-500 hover:text-red-500 font-medium">
            Clear cart
          </button>
        </div>

        {/* summary */}
        <div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sticky top-28">
            <h2 className="font-display font-bold text-lg text-slate-900">Order Summary</h2>

            <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3">
              <CreditCard className="h-5 w-5 text-blue-600 shrink-0" />
              <span className="text-sm text-slate-600">
                Card payment secured by <span className="font-semibold text-slate-900">Stripe</span>. GST calculated at checkout.
              </span>
            </div>

            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="font-semibold text-slate-900">${subtotal.toFixed(2)}</dd></div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Shipping</dt>
                <dd className="font-semibold text-slate-900">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">GST</dt>
                <dd className="text-slate-500">Calculated at checkout</dd>
              </div>
              <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                <dt className="font-display font-bold text-slate-900">Est. Total</dt>
                <dd className="font-display font-extrabold text-2xl text-slate-900">${estTotal.toFixed(2)}</dd>
              </div>
            </dl>

            <button
              onClick={checkout}
              disabled={placing}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white font-semibold py-3.5 hover:bg-blue-700 transition-colors disabled:opacity-70 active:scale-[0.99]"
            >
              {placing ? "Redirecting to Stripe…" : <>Checkout with Stripe <Lock className="h-4 w-4" /></>}
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <ShieldCheck className="h-3.5 w-3.5" /> 256-bit SSL · AUD pricing · Powered by Stripe
            </p>
            <Link to="/shop" className="mt-4 block text-center text-sm text-blue-600 font-semibold hover:underline">
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
